import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle,
  Edit,
  Flag,
  RefreshCw,
  Save,
  ShieldAlert,
  ToggleLeft,
  ToggleRight,
  X,
} from 'lucide-react';
import MainService from '../../services/MainService';

const emptyStatus = {
  Id: 0,
  Name: '',
  Keyword: '',
  Color: '#46955A',
  IsDriver: false,
};

const statusCodes = [
  { value: 'PKS', label: 'PKS - Pickup Schedule' },
  { value: 'PKT', label: 'PKT - Pickup Transit' },
  { value: 'PKD', label: 'PKD - Pickup Done' },
  { value: 'PKC', label: 'PKC - Pickup Cancelled' },
  { value: 'PKR', label: 'PKR - Pickup Return' },
  { value: 'INS', label: 'INS - In Store' },
  { value: 'INT', label: 'INT - Transit' },
  { value: 'DLV', label: 'DLV - Delivered' },
  { value: 'CNL', label: 'CNL - Cancelled' },
  { value: 'RTN', label: 'RTN - Returned' },
  { value: 'HDLV', label: 'HDLV - Half Delivered' },
  { value: 'HLD', label: 'HLD - Hold' },
];

const parseStatuses = (response) => {
  if (Array.isArray(response)) return response;
  if (Array.isArray(response?.Data)) return response.Data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

const isLockedStatus = (status) => ['cancelled', 'delivered'].includes(String(status?.Name || '').toLowerCase());

const Status = () => {
  const [statuses, setStatuses] = useState([]);
  const [formData, setFormData] = useState(emptyStatus);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const isEditing = Number(formData.Id) > 0;

  const filteredStatuses = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    if (!keyword) return statuses;
    return statuses.filter((status) =>
      String(status.Name || '').toLowerCase().includes(keyword) ||
      String(status.Keyword || '').toLowerCase().includes(keyword)
    );
  }, [searchTerm, statuses]);

  const loadStatuses = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await MainService.StatusInDropDown();
      const dropdownStatuses = parseStatuses(response);
      const hasColor = dropdownStatuses.some((status) => status.Color);

      if (hasColor) {
        setStatuses(dropdownStatuses);
        return;
      }

      const gridResponse = await MainService.StatusInGrid({ Skip: 0, Take: 1000 });
      setStatuses(parseStatuses(gridResponse));
    } catch (err) {
      console.error('Load statuses error:', err);
      setStatuses([]);
      setError('Failed to load statuses');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStatuses();
  }, [loadStatuses]);

  useEffect(() => {
    if (!error) return undefined;
    const timer = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = setTimeout(() => setSuccess(''), 3000);
    return () => clearTimeout(timer);
  }, [success]);

  const resetForm = () => {
    setFormData(emptyStatus);
  };

  const updateField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const editStatus = (status) => {
    if (isLockedStatus(status)) {
      setError('Status Cannot Edit');
      return;
    }

    setFormData({
      ...emptyStatus,
      ...status,
      Color: status.Color || '#46955A',
      IsDriver: status.IsDriver === true,
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const saveStatus = async () => {
    if (!formData.Name.trim()) {
      setError('Enter Name');
      return;
    }
    if (!formData.Keyword) {
      setError('Choose Code');
      return;
    }
    if (!formData.Color) {
      setError('Choose Color');
      return;
    }

    try {
      setSaving(true);
      setError('');
      const response = await MainService.SaveStatus(formData);
      if (response === 1 || response === '1' || response === true) {
        setSuccess('Success');
        resetForm();
        await loadStatuses();
      } else if (response === 2 || response === '2') {
        setError('Same status name already exists');
      } else {
        setError('Error');
      }
    } catch (err) {
      console.error('Save status error:', err);
      setError('Network Error');
    } finally {
      setSaving(false);
    }
  };

  const toggleDriverStatus = async (status) => {
    if (!status?.Id) return;

    try {
      setTogglingId(status.Id);
      setError('');
      const response = await MainService.EnableDriverStatus(status.Id);
      if (response === true || response === 'true') {
        setSuccess(status.IsDriver ? 'Driver disabled for status' : 'Driver enabled for status');
        await loadStatuses();
      } else {
        setError('Failed to update driver option');
      }
    } catch (err) {
      console.error('Toggle driver status error:', err);
      setError('Network Error');
    } finally {
      setTogglingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {error && <Toast type="error" message={error} onClose={() => setError('')} />}
      {success && <Toast type="success" message={success} onClose={() => setSuccess('')} />}

      <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex flex-col gap-4 border-b border-gray-200 pb-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-3 text-primary">
              <Flag className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Status</h1>
              <p className="mt-1 text-sm text-gray-500">Manage operational statuses and driver visibility.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={loadStatuses}
            disabled={loading}
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-gray-100 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh List
          </button>
        </div>

        <div className="mb-5 flex gap-3 rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
          <ShieldAlert className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <span>Warning: changing statuses may affect the operational flow. Please use this page carefully.</span>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-5">
            <h2 className="mb-4 text-lg font-semibold text-gray-900">{isEditing ? 'Update Status' : 'Add Status'}</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Name</label>
                <input
                  value={formData.Name}
                  onChange={(e) => updateField('Name', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Code</label>
                <select
                  value={formData.Keyword || ''}
                  onChange={(e) => updateField('Keyword', e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                >
                  <option value="">Select Code</option>
                  {statusCodes.map((code) => (
                    <option key={code.value} value={code.value}>{code.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Color</label>
                <div className="flex items-center gap-3">
                  <input
                    type="color"
                    value={formData.Color || '#46955A'}
                    onChange={(e) => updateField('Color', e.target.value)}
                    className="h-10 w-16 cursor-pointer rounded border border-gray-300 bg-white p-1"
                  />
                  <input
                    value={formData.Color || ''}
                    onChange={(e) => updateField('Color', e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary"
                    placeholder="#46955A"
                  />
                </div>
              </div>

              <label className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-gray-700">
                <input
                  type="checkbox"
                  checked={formData.IsDriver}
                  onChange={(e) => updateField('IsDriver', e.target.checked)}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                Is Driver?
              </label>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={resetForm}
                  disabled={saving}
                  className="inline-flex items-center gap-2 rounded-lg bg-white px-4 py-2 text-sm font-semibold text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50 disabled:opacity-50"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={saveStatus}
                  disabled={saving}
                  className="inline-flex min-w-28 items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                  {isEditing ? 'Update' : 'Save'}
                </button>
              </div>
            </div>
          </div>

          <div className="xl:col-span-2">
            <div className="rounded-xl border border-gray-200 bg-white">
              <div className="flex flex-col gap-3 border-b border-gray-200 p-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900">Status List</h2>
                  <p className="text-sm text-gray-500">All statuses are loaded at once.</p>
                </div>
                <input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-transparent focus:outline-none focus:ring-2 focus:ring-primary md:w-72"
                  placeholder="Search status / code"
                />
              </div>

              <div className="max-h-[650px] overflow-auto">
                <table className="w-full">
                  <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Name</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Keyword</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Color</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Driver?</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-500">Options</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {loading ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-16 text-center text-gray-500">
                          <RefreshCw className="mx-auto mb-2 h-8 w-8 animate-spin text-primary" />
                          Loading statuses...
                        </td>
                      </tr>
                    ) : filteredStatuses.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="px-4 py-16 text-center text-gray-500">No statuses found</td>
                      </tr>
                    ) : (
                      filteredStatuses.map((status) => {
                        const color = status.Color || '#9CA3AF';
                        return (
                          <tr key={status.Id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <div className="font-semibold text-gray-900">{status.Name || '-'}</div>
                              {isLockedStatus(status) && <div className="text-xs text-amber-600">Protected status</div>}
                            </td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{status.Keyword || '-'}</td>
                            <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">
                              <div className="flex items-center gap-2">
                                <span className="h-5 w-5 rounded-full border border-gray-200" style={{ backgroundColor: color }} />
                                <span className="font-mono text-xs">{status.Color || '-'}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => toggleDriverStatus(status)}
                                disabled={togglingId === status.Id}
                                className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold disabled:opacity-50 ${
                                  status.IsDriver
                                    ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                              >
                                {togglingId === status.Id ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : status.IsDriver ? (
                                  <ToggleRight className="h-4 w-4" />
                                ) : (
                                  <ToggleLeft className="h-4 w-4" />
                                )}
                                {status.IsDriver ? 'Enabled' : 'Disabled'}
                              </button>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                onClick={() => editStatus(status)}
                                className="rounded-lg p-2 text-yellow-600 hover:bg-yellow-50"
                                title="Edit"
                              >
                                <Edit className="h-4 w-4" />
                              </button>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Toast = ({ type, message, onClose }) => {
  const isError = type === 'error';
  return (
    <div className={`fixed right-4 top-4 z-50 flex min-w-[300px] items-center gap-3 rounded-lg border px-4 py-3 shadow-lg ${isError ? 'border-red-200 bg-red-50 text-red-700' : 'border-green-200 bg-green-50 text-green-700'}`}>
      {isError ? <AlertTriangle className="h-5 w-5 flex-shrink-0" /> : <CheckCircle className="h-5 w-5 flex-shrink-0" />}
      <span className="flex-1">{message}</span>
      <button type="button" onClick={onClose} className={isError ? 'text-red-500 hover:text-red-700' : 'text-green-500 hover:text-green-700'}>
        <X className="h-4 w-4" />
      </button>
    </div>
  );
};

export default Status;
